import React, {useCallback, useEffect, useState} from 'react';
import {useFocusEffect} from '@react-navigation/native';
import {View, Platform, ScrollView} from 'react-native';

import {StyleSheet} from 'react-native';
import {colorPalette} from '../../../core/config/color.config';

import {RNEButton, RNEText} from '../../components';
import {useGetNotifications} from '../../server/react-query/useNotification';
import useStores from '../../stores/useStores';
import {NotificationCard} from '../../views';
import {ActivityIndicator} from 'react-native';
import {width, height, totalSize} from 'react-native-dimension';
import {usePullDownRefreshContext} from '../../stores/pullDownRefreshStore';
import {applyNotificationBadgeCount} from '../../utils/notification-badge.utils';

const NotificationsScreen = () => {
  const {authStore, patientProfileStore} = useStores();
  const [isRefetching, setIsRefetching] = React.useState(false);

  const {
    data: notifications,
    isLoading,
    refetch,
  } = useGetNotifications({
    pageNumber: 1,
    pageSize: 1000,
    patientId:
      authStore.userRole === 'patient'
        ? patientProfileStore.activeProfile?.patientId
        : '',
  });

  // useEffect(() => {
  //   if (isFocused && Platform.OS !== 'web') {
  //     refetch();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isFocused]);

  useFocusEffect(
    useCallback(() => {
      let isActive = true; // to avoid setting state if screen unmounts

      const doRefetch = async () => {
        if (Platform.OS !== 'web') {
          setIsRefetching(true);
          try {
            await refetch(); // wait for server response
          } finally {
            if (isActive) setIsRefetching(false);
          }
        }
      };

      doRefetch();

      return () => {
        isActive = false;
      };
    }, [refetch]),
  );

  const {setCanRefresh} = usePullDownRefreshContext();

  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useFocusEffect(
    useCallback(() => {
      if (scrollViewHeight > 0 && contentHeight > 0) {
        const isScrollable = contentHeight > scrollViewHeight;

        if (!isScrollable) {
          setCanRefresh(true); // always allow pull-down if not scrollable
        } else {
          setCanRefresh(scrollY <= 0); // only allow when scrolled to top
        }
      }
    }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]),
  );
  const handleScroll = (e: any) => {
    const y = e.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;

    if (!isScrollable) {
      setCanRefresh(true); // still always true if no scroll
    } else {
      setCanRefresh(y <= 0); // only true when back at the very top
    }
  };

  useEffect(() => {
    if (Platform.OS !== 'web') {
      void applyNotificationBadgeCount(notifications?.totalUnread ?? 0);
    }
  }, [notifications?.totalUnread]);

  return (
    <>
      {Platform.OS === 'web' ? (
        <View style={webStyles.mainContainer}>
          <View>
            <RNEText
              label="Notifications"
              variant="header"
              weight="bold"
              color="accent"
            />
          </View>
          <ScrollView>
            {isLoading ? (
              <RNEButton
                height={60}
                loadingProps={loadingPropsStyle}
                loading={true}
              />
            ) : (
              <View style={webStyles.cardsContainer}>
                {notifications && notifications.totalCount > 0 ? (
                  notifications.data.map((notification, i) => (
                    <NotificationCard notification={notification} key={i} />
                  ))
                ) : (
                  <View style={sharedStyles.emptyNotifications}>
                    <RNEText
                      label="No notifications at the moment"
                      variant="body"
                      align="center"
                      color="black"
                    />
                  </View>
                )}
              </View>
            )}
          </ScrollView>
        </View>
      ) : (
        <ScrollView
          onScroll={handleScroll}
          onLayout={e => {
            setScrollViewHeight(e.nativeEvent.layout.height);
          }}
          onContentSizeChange={(contentWidth, contentHeight) => {
            setContentHeight(contentHeight);
          }}>
          <View style={mobileStyles.mainContainer}>
            <View>
              <RNEText
                label="Notifications"
                variant="header"
                weight="bold"
                color="accent"
                style={{fontSize: totalSize(3.2)}}
              />
            </View>
            {(isLoading || isRefetching) && (
              <ActivityIndicator
                size={totalSize(4)}
                color={colorPalette.secondary}
                style={{marginTop: height(3), justifyContent: 'center'}}
              />
            )}

            {!isLoading && !isRefetching && (
              <View style={mobileStyles.cardsContainer}>
                {notifications && notifications.totalCount > 0 ? (
                  notifications.data.map((notification, i) => (
                    <React.Fragment key={notification.notificationId || i}>
                      {notification?.type !== 13 && (
                        <NotificationCard notification={notification} />
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <View style={sharedStyles.emptyNotifications}>
                    <RNEText
                      label="No notifications at the moment"
                      variant="body"
                      align="center"
                      color="black"
                    />
                  </View>
                )}
              </View>
            )}
          </View>
        </ScrollView>
      )}
    </>
  );
};

export default NotificationsScreen;

const loadingPropsStyle = {
  color: colorPalette.primary,
};

const webStyles = StyleSheet.create({
  mainContainer: {
    paddingVertical: 140,
    paddingHorizontal: 80,
    width: '100%',
    flex: 1,
  },
  cardsContainer: {
    paddingRight: 100,
    marginLeft: -20,
  },
});

const mobileStyles = StyleSheet.create({
  mainContainer: {
    paddingTop: 0,
    padding: totalSize(2),
    width: '100%',
  },
  cardsContainer: {
    marginLeft: -45,
    // width: '100%',
    margin: 0,
    padding: 0,
    // backgroundColor:'red'
  },
});

const sharedStyles = StyleSheet.create({
  emptyNotifications: {
    marginTop: 20,
  },
});
